import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Swal from "sweetalert2";
import { ModalOverlay, ModalContent2 } from "../../styles/common";
import { useDispatch, useSelector } from "react-redux";
import { loginSuccess, logout } from "../../store/slices/auth";

const PageContainer = styled.div`
  display: flex;
  height: 100vh;
  width: 100%;
  font-family: "Inter", sans-serif;
  background: #f8fafc;

  @media (max-width: 900px) {
    flex-direction: column;
    height: auto;
  }
`;

const LeftPanel = styled.div`
  flex: 0 0 40%;
  background: #30D5C8;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 60px;
  border-radius: 0 40px 40px 0;

  img {
    width: 180px;
    filter: invert(1);
    margin-bottom: 20px;
  }

  @media (max-width: 900px) {
    flex: none;
    width: 100%;
    border-radius: 0 0 40px 40px;
    padding: 40px 20px;
    align-items: center;
    text-align: center;

    img {
      width: 150px;
      margin-bottom: 16px;
    }
  }
`;

const LeftTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 16px;

  @media (max-width: 900px) {
    font-size: 28px;
  }
`;

const LeftText = styled.p`
  font-size: 16px;
  line-height: 1.6;
  opacity: 0.9;
  max-width: 400px;

  @media (max-width: 900px) {
    max-width: 100%;
  }
`;

const RightPanel = styled.div`
  flex: 1;
  background: #f9fafc;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 60px;
  min-height: 100vh;
  overflow-y: auto;

  @media (max-width: 900px) {
    padding: 30px 20px 80px;
    min-height: auto;
  }
`;

const FormContainer = styled.form`
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const FormTitle = styled.h2`
  font-size: 28px;
  color: #111;
  margin-bottom: 10px;
  @media (max-width: 900px) {
    text-align: center;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  @media (max-width: 700px) {
    flex-direction: column;
  }
`;

const InputField = styled.input`
  flex: 1;
  padding: 14px;
  border: 1px solid #d0d5dd;
  border-radius: 10px;
  font-size: 15px;
  &:focus {
    border-color: #30D5C8;
    outline: none;
    box-shadow: 0 0 4px rgba(239, 68, 68, 0.3);
  }
`;

const TextArea = styled.textarea`
  flex: 1;
  padding: 14px;
  border: 1px solid #d0d5dd;
  border-radius: 10px;
  font-size: 15px;
  resize: none;
  min-height: 80px;
  &:focus {
    border-color: #30D5C8;
    outline: none;
    box-shadow: 0 0 4px rgba(239, 68, 68, 0.3);
  }
`;

const Button = styled.button`
  background: #30D5C8;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 14px;
  font-size: 16px;
  cursor: pointer;
  margin-top: 10px;
  transition: 0.3s;

  &:hover {
    background: #dc2626;
  }
`;

const FileRow = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
`;

const FileUploadWrapper = styled.div`
  flex: 1;
  min-width: 200px;
`;

const FilePreview = styled.div`
  position: relative;
  margin-top: 8px;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid #d0d5dd;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  cursor: pointer;
  font-size: 14px;
  line-height: 22px;
`;

const UploadLabel = styled.label`
  display: block;
  padding: 12px;
  border: 2px dashed #d0d5dd;
  border-radius: 10px;
  text-align: center;
  cursor: pointer;
  color: #555;
  font-size: 14px;
  &:hover {
    border-color: #30D5C8;
    color: #30D5C8;
  }
`;

const PdfPreview = styled.div`
  width: 100%;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border-radius: 10px;
  border: 1px solid #d0d5dd;
  background: #f1f5f9;
  color: #334155;
  font-size: 14px;
  word-break: break-all;
  text-align: center;
`;

const SelectField = styled.select`
  flex: 1;
  padding: 14px;
  border: 1px solid #d0d5dd;
  border-radius: 10px;
  font-size: 15px;

  &:focus {
    border-color: #30D5C8;
    outline: none;
  }
`;

const LocationPicker = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MapSearchRow = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 700px) {
    flex-direction: column;
  }
`;

const MapSearchInput = styled(InputField)`
  width: 100%;
`;

const MapSearchBox = styled.div`
  position: relative;
  flex: 1;
`;

const SuggestionsList = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 6px);
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 10px;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.14);
  overflow: hidden;
  z-index: 20;
`;

const SuggestionItem = styled.button`
  width: 100%;
  border: 0;
  background: #ffffff;
  color: #334155;
  padding: 12px 14px;
  font-size: 14px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #ecfeff;
    color: #0f766e;
  }
`;

const MapButton = styled.button`
  border: 1px solid #30D5C8;
  background: #ffffff;
  color: #0f766e;
  padding: 0 16px;
  border-radius: 10px;
  min-height: 48px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #ecfeff;
  }
`;

const MapCanvas = styled.div`
  width: 100%;
  height: 280px;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  overflow: hidden;
  background: #e2e8f0;
`;

const MapHelpText = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const VENDOR_DOC_ACCEPT = "image/jpeg,image/jpg,image/png,application/pdf,.pdf,.jpg,.jpeg,.png";

let googleMapsLoaderPromise;

const loadGoogleMaps = (apiKey) => {
  if (window.google?.maps) {
    return Promise.resolve(window.google);
  }

  if (!googleMapsLoaderPromise) {
    googleMapsLoaderPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector("script[data-google-maps='true']");
      if (existingScript) {
        existingScript.addEventListener("load", () => resolve(window.google));
        existingScript.addEventListener("error", reject);
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.dataset.googleMaps = "true";
      script.onload = () => resolve(window.google);
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  return googleMapsLoaderPromise;
};

const isAllowedVendorDocument = (file) => {
  if (!file) return false;
  const allowedMimes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
  const ext = file.name?.split(".").pop()?.toLowerCase();
  const allowedExt = ["jpg", "jpeg", "png", "pdf"];
  return allowedMimes.includes(file.type) || allowedExt.includes(ext);
};

const isImageFile = (file) =>
  file?.type?.startsWith("image/") || /\.(jpe?g|png)$/i.test(file?.name || "");

export default function BusinessSetupForm() {
  const auth = useSelector(state => state.auth)
  const dispatch = useDispatch();
  console.log("--->auth",auth)
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [form, setForm] = useState({
    legalName: auth.fullname,
    businessType: "",
    gst: "",
    pan: "",
    registeredAddress: "",
    address: "",
    houseFloorStreet: "",
    latitude: "",
    longitude: "",
    state: "",
    city: "",
    phone: "",
    email: auth.email,
    accountHolder: "",
    bankName: "",
    accountNumber: "",
    ifsc: "",
    fullName: "",
    cancelledCheque: null,
    govtId: null,
    tradeLicense: null,
    digitalSignature: null,
  });
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const [mapSearch, setMapSearch] = useState("");
  const [mapSuggestions, setMapSuggestions] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const getFullAddress = useCallback((currentForm = form) => {
    return [
      currentForm.houseFloorStreet,
      currentForm.registeredAddress,
    ]
      .filter(Boolean)
      .join(", ");
  }, [form]);

  const updateLocationFields = useCallback((lat, lng, address = "") => {
    const latitude = lat?.toString() || "";
    const longitude = lng?.toString() || "";

    setForm((prev) => ({
      ...prev,
      latitude,
      longitude,
      registeredAddress: address || prev.registeredAddress,
      address: address || prev.address,
    }));
  }, []);

  const setLocationFromLatLng = useCallback((latLng, fallbackAddress = "") => {
    const lat = latLng.lat();
    const lng = latLng.lng();
    const geocoder = geocoderRef.current;

    if (!geocoder) {
      updateLocationFields(lat, lng, fallbackAddress);
      return;
    }

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      const address =
        status === "OK" && results?.[0]?.formatted_address
          ? results[0].formatted_address
          : fallbackAddress;

      updateLocationFields(lat, lng, address);
    });
  }, [updateLocationFields]);

  const moveMarker = useCallback((latLng, address = "") => {
    if (!mapInstanceRef.current || !markerRef.current) return;

    markerRef.current.setPosition(latLng);
    mapInstanceRef.current.panTo(latLng);
    mapInstanceRef.current.setZoom(Math.max(mapInstanceRef.current.getZoom() || 15, 15));
    setLocationFromLatLng(latLng, address);
  }, [setLocationFromLatLng]);

  const handleUseCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) return;

    setIsFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const latLng = new window.google.maps.LatLng(coords.latitude, coords.longitude);
        moveMarker(latLng);
        setIsFetchingLocation(false);
      },
      (error) => {
        console.error("Location access failed", error);
        setIsFetchingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [moveMarker]);

  const handleMapSearchChange = (event) => {
    setMapSearch(event.target.value);
  };

  const handleSuggestionClick = (suggestion) => {
    if (!geocoderRef.current || !window.google?.maps) return;

    setMapSearch(suggestion.description);
    setMapSuggestions([]);

    geocoderRef.current.geocode({ placeId: suggestion.place_id }, (results, status) => {
      if (status !== "OK" || !results?.[0]?.geometry?.location) return;

      moveMarker(
        results[0].geometry.location,
        results[0].formatted_address || suggestion.description
      );
    });
  };

  useEffect(() => {
    const reverseGeocode = async (lat, lng) => {
      try {
        const geoResponse = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`
        );
        if (!geoResponse.ok) return "";
        const geoData = await geoResponse.json();
        return geoData?.display_name || "";
      } catch (error) {
        console.error("Reverse geocode failed", error);
        return "";
      }
    };

    const fetchLocation = () => {
      if (!navigator.geolocation) return;
      setIsFetchingLocation(true);

      navigator.geolocation.getCurrentPosition(
        async ({ coords }) => {
          const lat = coords.latitude?.toString() || "";
          const lng = coords.longitude?.toString() || "";
          const detectedAddress = await reverseGeocode(lat, lng);

          setForm((prev) => ({
            ...prev,
            latitude: prev.latitude || lat,
            longitude: prev.longitude || lng,
            registeredAddress: prev.registeredAddress || detectedAddress,
            address: prev.address || detectedAddress,
          }));
          setIsFetchingLocation(false);
        },
        (error) => {
          console.error("Location access failed", error);
          setIsFetchingLocation(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    };

    fetchLocation();
  }, []);

  useEffect(() => {
    if (!googleMapsApiKey || !mapRef.current) return;
    if (mapInstanceRef.current) return;

    let isCancelled = false;

    loadGoogleMaps(googleMapsApiKey)
      .then((google) => {
        if (isCancelled || !mapRef.current) return;

        const initialLat = Number(form.latitude);
        const initialLng = Number(form.longitude);
        const hasInitialLocation = Number.isFinite(initialLat) && Number.isFinite(initialLng);
        const initialPosition = hasInitialLocation
          ? { lat: initialLat, lng: initialLng }
          : { lat: 20.5937, lng: 78.9629 };

        geocoderRef.current = new google.maps.Geocoder();
        mapInstanceRef.current = new google.maps.Map(mapRef.current, {
          center: initialPosition,
          zoom: hasInitialLocation ? 15 : 5,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });

        markerRef.current = new google.maps.Marker({
          map: mapInstanceRef.current,
          position: initialPosition,
          draggable: true,
          title: "Selected business location",
        });

        mapInstanceRef.current.addListener("click", (event) => {
          if (event.latLng) {
            moveMarker(event.latLng);
          }
        });

        markerRef.current.addListener("dragend", (event) => {
          if (event.latLng) {
            setLocationFromLatLng(event.latLng);
          }
        });

        setIsMapReady(true);
      })
      .catch((error) => {
        console.error("Google Maps failed to load", error);
      });

    return () => {
      isCancelled = true;
    };
  }, [form.latitude, form.longitude, googleMapsApiKey, moveMarker, setLocationFromLatLng]);

  useEffect(() => {
    if (!isMapReady || !window.google?.maps || !markerRef.current || !mapInstanceRef.current) return;

    const lat = Number(form.latitude);
    const lng = Number(form.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const latLng = new window.google.maps.LatLng(lat, lng);
    markerRef.current.setPosition(latLng);
    mapInstanceRef.current.setCenter(latLng);
  }, [form.latitude, form.longitude, isMapReady]);

  useEffect(() => {
    if (!isMapReady || !geocoderRef.current || !window.google?.maps) return;

    const query = mapSearch.trim();
    if (query.length < 3) return;

    const timeoutId = setTimeout(() => {
      geocoderRef.current.geocode(
        {
          address: query,
          componentRestrictions: { country: "IN" },
        },
        (results, status) => {
          if (status !== "OK" || !results?.[0]?.geometry?.location) return;

          moveMarker(results[0].geometry.location, results[0].formatted_address || query);
        }
      );
    }, 700);

    return () => clearTimeout(timeoutId);
  }, [isMapReady, mapSearch, moveMarker]);

  useEffect(() => {
    if (!isMapReady || !window.google?.maps?.places) return;

    const query = mapSearch.trim();
    if (query.length < 2) {
      setMapSuggestions([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        {
          input: query,
          componentRestrictions: { country: "in" },
        },
        (predictions, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions?.length) {
            setMapSuggestions([]);
            return;
          }

          setMapSuggestions(predictions.slice(0, 5));
        }
      );
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [isMapReady, mapSearch]);

  useEffect(() => {
  const fetchStates = async () => {
    try {
      const res = await fetch(
        "https://countriesnow.space/api/v0.1/countries/states",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            country: "India",
          }),
        }
      );

      const data = await res.json();
      setStates(data.data.states || []);
    } catch (err) {
      console.error(err);
    }
  };

  fetchStates();
}, []);


const handleStateChange = async (e) => {
  const state = e.target.value;

  setForm((prev) => ({
    ...prev,
    state,
    city: "",
  }));

  try {
    const res = await fetch(
      "https://countriesnow.space/api/v0.1/countries/state/cities",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          country: "India",
          state,
        }),
      }
    );

    const data = await res.json();
    setCities(data.data || []);
  } catch (err) {
    console.error(err);
  }
};

    const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === "file") {
      const file = files?.[0];
      if (file && !isAllowedVendorDocument(file)) {
        alert("Please upload a JPG, PNG image or PDF file.");
        e.target.value = "";
        return;
      }
    }

    setForm((prevForm) => ({
      ...prevForm,
      [name]:
        type === "checkbox"
          ? checked
          : type === "file"
            ? files[0]
            : name === "sortOrder"
              ? Number(value)
              : value,
    }));
  };


  const validateForm = (form) => {
  const errors = {};

  if (!form.legalName.trim()) errors.legalName = "Legal name is required";
  if (!form.businessType) errors.businessType = "Business type is required";
  if (!form.city) errors.city = "City type is required";
  if (!form.state) errors.state = "State type is required";

  if (!form.gst) errors.gst = "GST number is required";
  else if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(form.gst))
    errors.gst = "Invalid GST format";

  if (!form.pan) errors.pan = "PAN is required";
  else if (!/[A-Z]{5}[0-9]{4}[A-Z]{1}/.test(form.pan))
    errors.pan = "Invalid PAN format";

  if (!form.registeredAddress.trim()) errors.registeredAddress = "Registered address is required";
  if (!form.houseFloorStreet.trim()) errors.houseFloorStreet = "House/Floor, building or street locality is required";
  if (!form.latitude) errors.latitude = "Latitude is required";
  if (!form.longitude) errors.longitude = "Longitude is required";

  if (!form.accountHolder.trim()) errors.accountHolder = "Account holder name is required";
  if (!form.bankName.trim()) errors.bankName = "Bank name is required";
  if (!form.accountNumber.trim()) errors.accountNumber = "Account number is required";

  if (!form.ifsc) errors.ifsc = "IFSC is required";
  else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.ifsc))
    errors.ifsc = "Invalid IFSC code";

  if (!form.cancelledCheque && !isAllowedVendorDocument(form.cancelledCheque))
    errors.cancelledCheque = "Cancelled cheque must be a JPG, PNG image or PDF";
  if (!form.govtId && !isAllowedVendorDocument(form.govtId))
    errors.govtId = "Government ID must be a JPG, PNG image or PDF";
  if (!form.tradeLicense && !isAllowedVendorDocument(form.tradeLicense))
    errors.tradeLicense = "GST cert must be a JPG, PNG image or PDF";
  if (form.digitalSignature && !isAllowedVendorDocument(form.digitalSignature))
    errors.digitalSignature = "Digital signature must be a JPG, PNG image or PDF";

  return errors;
};


  const handleSubmit = async (e) => {
    e.preventDefault();

  const errors = validateForm(form);
  if (Object.keys(errors).length > 0) {
    console.log("errors",errors)
    let message = Object.values(errors).join("\n");
    console.log("errors",message)
    alert(message)
    // Swal.fire("Success!", "Business information submitted successfully.", "success");
    return;
  }

    const fullAddress = getFullAddress(form);
    const formData = new FormData();
    formData.append("legalName", form.legalName);
    formData.append("businessType", form.businessType);
    formData.append("gst", form.gst);
    formData.append("pan", form.pan);
    formData.append("email", auth.email);
    formData.append("registeredAddress", fullAddress);
    formData.append("address", fullAddress);
    formData.append("mapAddress", form.registeredAddress);
    formData.append("houseFloorStreet", form.houseFloorStreet);
    formData.append("latitude", form.latitude);
    formData.append("longitude", form.longitude);
    formData.append("accountHolder", form.accountHolder);
    formData.append("bankName", form.bankName);
    formData.append("accountNumber", form.accountNumber);
    formData.append("ifsc", form.ifsc);
    formData.append("isDocsPending", true);
    formData.append("state", form.state);
    formData.append("city", form.city);
    if(form.cancelledCheque){
      formData.append("cancelledCheque", form.cancelledCheque);
    }
    if(form.govtId){
      formData.append("govtId", form.govtId);
    }
    if(form.tradeLicense){
      formData.append("tradeLicense", form.tradeLicense);
    }
    if(form.digitalSignature){
      formData.append("digitalSignature", form.digitalSignature);
    }

    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}vendor/update`, {
          method: "POST",
          body: formData,
        });
        const data = await response.json(); // ✅ Parse JSON body
        console.log("Response JSON:", data);
        if(data.message === "SUCCESS_FULLY_CREATED"){
          dispatch(
            loginSuccess({
              isDocsPending: true,
            })
          );
          alert("Your documents have been submitted successfully. Please wait while our team reviews them.")
          window.location.reload()
        }
      // if (!res.ok) throw new Error("Failed to submit business info");
      // Swal.fire("Success!", "Business information submitted successfully.", "success");
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Something went wrong while submitting.", "error");
    }
  };
    function logOut() {
      dispatch(
        logout()
      )
    }

  return (
    <ModalOverlay>
      <ModalContent2>
        <PageContainer>
          <LeftPanel>
            <img src="/assets/logo.png" alt="logo" />
            <LeftTitle>Let's set up your Business Profile</LeftTitle>
            <LeftText>
              Provide your business, banking, and verification details. These help us verify and
              activate your business account.
            </LeftText>
          </LeftPanel>

          <RightPanel>
            <FormContainer onSubmit={handleSubmit}>
              <div style={{display:"flex",justifyContent:"space-between"}}>
                  <FormTitle>Let’s get started</FormTitle>
                  <span style={{color:"black",pointer:"cursor"}} onClick={() => logOut()}>Logout</span>
              </div>
              <InputField name="legalName" disabled placeholder="Legal Business Name" value={form.legalName} onChange={handleChange} />
              <Row>
                <InputField name="businessType" placeholder="Business Type" value={form.businessType} onChange={handleChange} />
                <InputField name="gst" placeholder="GST / VAT / Tax ID" value={form.gst} onChange={handleChange} />
                <InputField name="pan" placeholder="PAN (or equivalent tax number)" value={form.pan} onChange={handleChange} />
              </Row>

                <Row>
                  <SelectField
                    name="state"
                    value={form.state}
                    onChange={handleStateChange}
                  >
                    <option value="">Select State</option>

                    {states.map((state) => (
                      <option key={state.name} value={state.name}>
                        {state.name}
                      </option>
                    ))}
                  </SelectField>

                  <SelectField
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    disabled={!form.state}
                  >
                    <option value="">Select City</option>

                    {cities.map((city) => (
                      <option key={city} value={city}>
                        {city.normalize("NFD").replace(/[\u0300-\u036f]/g, "")}
                      </option>
                    ))}
                  </SelectField>

                </Row>
              <Row>
                <LocationPicker>
                  <MapSearchRow>
                    <MapSearchBox>
                      <MapSearchInput
                        type="text"
                        placeholder="Search and pick business location"
                        value={mapSearch}
                        onChange={handleMapSearchChange}
                        disabled={!googleMapsApiKey}
                      />
                      {mapSuggestions.length > 0 && (
                        <SuggestionsList>
                          {mapSuggestions.map((suggestion) => (
                            <SuggestionItem
                              key={suggestion.place_id}
                              type="button"
                              onClick={() => handleSuggestionClick(suggestion)}
                            >
                              {suggestion.description}
                            </SuggestionItem>
                          ))}
                        </SuggestionsList>
                      )}
                    </MapSearchBox>
                    <MapButton
                      type="button"
                      onClick={handleUseCurrentLocation}
                      disabled={!isMapReady || isFetchingLocation}
                    >
                      {isFetchingLocation ? "Detecting..." : "Use Current Location"}
                    </MapButton>
                  </MapSearchRow>
                  {googleMapsApiKey ? (
                    <>
                      <MapCanvas ref={mapRef} />
                      <MapHelpText>
                        Search an address, click on the map, or drag the marker to set the exact business location.
                      </MapHelpText>
                    </>
                  ) : (
                    <MapHelpText>
                      Add REACT_APP_GOOGLE_MAPS_API_KEY in adminfrontend .env to enable Google Maps location picker.
                    </MapHelpText>
                  )}
                  <Row>
                    <InputField
                      name="houseFloorStreet"
                      placeholder="House/Floor No, Building Name or Street Locality"
                      value={form.houseFloorStreet}
                      onChange={handleChange}
                    />
                    {/* <InputField
                      name="landmark"
                      placeholder="Landmark"
                      value={form.landmark}
                      onChange={handleChange}
                    /> */}
                  </Row>
                  <TextArea
                    name="registeredAddress"
                    placeholder="Full Address"
                    value={getFullAddress()}
                    readOnly
                  />
                </LocationPicker>
              </Row>
              <Row>
                <InputField
                  name="latitude"
                  placeholder={isFetchingLocation ? "Fetching latitude..." : "Latitude"}
                  value={form.latitude}
                  onChange={handleChange}
                />
                <InputField
                  name="longitude"
                  placeholder={isFetchingLocation ? "Fetching longitude..." : "Longitude"}
                  value={form.longitude}
                  onChange={handleChange}
                />
              </Row>

              <Row>
                <InputField name="email" disabled placeholder="Business Email" type="email" value={form.email} onChange={handleChange} />
                <InputField name="phone" placeholder="Business Phone" value={form.phone} onChange={handleChange} />
                <InputField name="fullName" placeholder="Primary Contact Person" value={form.fullName} onChange={handleChange} />
              </Row>

              <Row>
                <InputField name="accountHolder" placeholder="Account Holder Name" value={form.accountHolder} onChange={handleChange} />
                <InputField type="number" name="accountNumber" placeholder="Account Number" value={form.accountNumber} onChange={handleChange} />
                <InputField name="bankName" placeholder="Bank Name & Branch" value={form.bankName} onChange={handleChange} />
                <InputField name="ifsc" placeholder="IFSC / SWIFT Code" value={form.ifsc} onChange={handleChange} />
              </Row>

              {/* File Uploads */}
              {/* <FileRow>
                {["cancelledCheque", "govtId", "tradeLicense", "digitalSignature"].map((key) => (
                  <FileUploadWrapper key={key}>
                    <UploadLabel htmlFor={key}>
                      {key === "cancelledCheque"
                        ? "Cancelled Cheque / Bank Letter"
                        : key === "govtId"
                        ? "Government ID"
                        : key === "tradeLicense"
                        ? "Trade License / Incorporation"
                        : "Digital Signature (Optional)"}
                    </UploadLabel>
                    <input
                      type="file"
                      id={key}
                      name={key}
                      accept=".pdf,.jpg,.png"
                      style={{ display: "none" }}
                      onChange={handleFileChange}
                    />
                    {files[key] && (
                      <FilePreview>
                        {files[key].type.startsWith("image") ? (
                          <PreviewImage src={URL.createObjectURL(files[key])} alt={key} />
                        ) : (
                          <p>{files[key].name}</p>
                        )}
                        <RemoveButton onClick={() => setFiles({ ...files, [key]: null })}>×</RemoveButton>
                      </FilePreview>
                    )}
                  </FileUploadWrapper>
                ))}
              </FileRow> */}

            <FileRow>
  {/* Cancelled Cheque */}
  <FileUploadWrapper>
    <UploadLabel htmlFor="cancelledCheque">Cancelled Cheque / Bank Letter (JPG, PNG or PDF)</UploadLabel>
    <input
      type="file"
      id="cancelledCheque"
      name="cancelledCheque"
      accept={VENDOR_DOC_ACCEPT}
      style={{ display: "none" }}
      onChange={handleChange}
    />
    {form.cancelledCheque && (
      <FilePreview>
        {isImageFile(form.cancelledCheque) ? (
          <PreviewImage src={URL.createObjectURL(form.cancelledCheque)} alt="cancelledCheque" />
        ) : (
          <PdfPreview>
            <span>PDF</span>
            <span>{form.cancelledCheque.name}</span>
          </PdfPreview>
        )}
        <RemoveButton
          type="button"
          onClick={() => setForm({ ...form, cancelledCheque: null })}
        >
          ×
        </RemoveButton>
      </FilePreview>
    )}
  </FileUploadWrapper>

  {/* Government ID */}
  <FileUploadWrapper>
    <UploadLabel htmlFor="govtId">Government ID (JPG, PNG or PDF)</UploadLabel>
    <input
      type="file"
      id="govtId"
      name="govtId"
      accept={VENDOR_DOC_ACCEPT}
      style={{ display: "none" }}
      onChange={handleChange}
    />
    {form.govtId && (
      <FilePreview>
        {isImageFile(form.govtId) ? (
          <PreviewImage src={URL.createObjectURL(form.govtId)} alt="govtId" />
        ) : (
          <PdfPreview>
            <span>PDF</span>
            <span>{form.govtId.name}</span>
          </PdfPreview>
        )}
        <RemoveButton
          type="button"
          onClick={() => setForm({ ...form, govtId: null })}
        >
          ×
        </RemoveButton>
      </FilePreview>
    )}
  </FileUploadWrapper>

  {/* Trade License */}
  <FileUploadWrapper>
    <UploadLabel htmlFor="tradeLicense">GST Cert (JPG, PNG or PDF)</UploadLabel>
    <input
      type="file"
      id="tradeLicense"
      name="tradeLicense"
      accept={VENDOR_DOC_ACCEPT}
      style={{ display: "none" }}
      onChange={handleChange}
    />
    {form.tradeLicense && (
      <FilePreview>
        {isImageFile(form.tradeLicense) ? (
          <PreviewImage src={URL.createObjectURL(form.tradeLicense)} alt="tradeLicense" />
        ) : (
          <PdfPreview>
            <span>PDF</span>
            <span>{form.tradeLicense.name}</span>
          </PdfPreview>
        )}
        <RemoveButton
          type="button"
          onClick={() => setForm({ ...form, tradeLicense: null })}
        >
          ×
        </RemoveButton>
      </FilePreview>
    )}
  </FileUploadWrapper>

  {/* Digital Signature */}
  <FileUploadWrapper>
    <UploadLabel htmlFor="digitalSignature">Digital Signature (JPG, PNG or PDF)</UploadLabel>
    <input
      type="file"
      id="digitalSignature"
      name="digitalSignature"
      accept={VENDOR_DOC_ACCEPT}
      style={{ display: "none" }}
      onChange={handleChange}
    />
    {form.digitalSignature && (
      <FilePreview>
        {isImageFile(form.digitalSignature) ? (
          <PreviewImage src={URL.createObjectURL(form.digitalSignature)} alt="digitalSignature" />
        ) : (
          <PdfPreview>
            <span>PDF</span>
            <span>{form.digitalSignature.name}</span>
          </PdfPreview>
        )}
        <RemoveButton
          type="button"
          onClick={() => setForm({ ...form, digitalSignature: null })}
        >
          ×
        </RemoveButton>
      </FilePreview>
    )}
  </FileUploadWrapper>
</FileRow>



              <Button type="submit">Submit Details →</Button>
            </FormContainer>
          </RightPanel>
        </PageContainer>
      </ModalContent2>
    </ModalOverlay>
  );
}
